import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaEnvelope, FaGithub, FaLinkedin, FaPlay, FaYoutube } from "react-icons/fa";
import SiteLayout from "../../components/layout/SiteLayout";
import { ActionLink, EditorialSection, SectionHeader } from "../../components/ui/EditorialSection";
import { getGDriveFile, getGDriveFiles, getYoutubeVideos } from "../../services/api/http";
import "./HomePage.css";

const asset = (name) => `${process.env.PUBLIC_URL}/assets/${name}`;

const videoId = (link) => {
  try {
    const url = new URL(link);
    return url.searchParams.get("v") || url.pathname.split("/").filter(Boolean).pop();
  } catch (error) {
    return null;
  }
};

const mimeType = (name) => {
  const value = String(name || "").toLowerCase();
  if (value.endsWith(".png")) return "image/png";
  if (value.endsWith(".jpg") || value.endsWith(".jpeg")) return "image/jpeg";
  if (value.endsWith(".webp")) return "image/webp";
  return "application/octet-stream";
};

const experience = [
  ["Mar 2026 — Present", "Intuit", "Staff Software Engineer", "Digital assets platform work across Java, PostgreSQL, React, and distributed microservices."],
  ["Dec 2019 — Mar 2026", "Goldman Sachs", "Vice President / Lead Software Engineer", "Technical leadership across trading, risk, event-driven systems, and React modernization."],
  ["Jan 2015 — Nov 2019", "The Princeton Review", "Senior Software Engineer", "Large-scale student dashboards, exam simulation, recommendation, and messaging platforms."],
];

const projects = [
  {
    title: "Body Analyzer",
    type: "iOS application",
    copy: "Health and body-composition tracking.",
    href: "https://apps.apple.com/us/app/body-analyzer-plus/id1489053077?itsct=apps_box_badge&itscg=30200",
    image: "https://is3-ssl.mzstatic.com/image/thumb/Purple124/v4/92/a2/4d/92a24dfc-1331-de5d-2896-0225387d4c09/AppIcon-0-0-1x_U007emarketing-0-0-0-7-0-0-sRGB-0-0-0-GLES2_U002c0-512MB-85-220-0-0.png/460x0w.webp",
  },
  {
    title: "Meditation Counter",
    type: "iOS application",
    copy: "A focused tool for tracking repetitions.",
    href: "https://apps.apple.com/us/app/meditation-counter/id1559832205",
    image: "https://is1-ssl.mzstatic.com/image/thumb/Purple126/v4/06/5f/70/065f70c2-cbb2-ab55-8a91-e2c063b0420d/AppIcon-0-0-1x_U007emarketing-0-7-0-0-85-220.png/180x0w.png",
  },
];

function HomePage() {
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    let mounted = true;
    const objectUrls = [];

    getYoutubeVideos()
      .then(({ data = [] }) => {
        if (!mounted) return;
        setVideos(data.slice(0, 3).map((item) => {
          const id = videoId(item.webContentLink);
          return {
            title: item.name,
            description: item.description || "Watch now",
            href: id ? `https://www.youtube.com/watch?v=${id}` : item.webContentLink,
            thumbnail: id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null,
          };
        }));
      })
      .catch(() => mounted && setVideos([]));

    getGDriveFiles()
      .then(async ({ data = [] }) => {
        const candidates = data.filter((item) => !/\.(heic|heif)$/i.test(item.name || "")).slice(0, 2);
        const loaded = await Promise.all(candidates.map(async (item) => {
          try {
            const response = await getGDriveFile(item.id);
            const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType(item.name) }));
            objectUrls.push(url);
            return { id: item.id, name: item.name, src: url };
          } catch (error) {
            return null;
          }
        }));
        if (mounted) setPhotos(loaded.filter(Boolean));
      })
      .catch(() => mounted && setPhotos([]));

    return () => {
      mounted = false;
      objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, []);

  return (
    <SiteLayout>
      <main className="home-page">
        <section className="home-hero" style={{ "--hero-image": `url(${asset("main-portrait.png")})` }} aria-labelledby="home-title">
          <div className="home-hero-inner">
            <div className="home-hero-copy">
              <span className="home-eyebrow">Vishwaram Maharaj</span>
              <h1 id="home-title">Vishwaram Maharaj</h1>
              <p className="home-hero-tagline">Where Technology Meets Tradition.</p>
              <p className="home-hero-description">Bridging technology and tradition through engineering, leadership, and spiritual service.</p>
              <div className="home-hero-actions">
                <a className="home-button home-button-primary" href="#technology">Explore My Work <FaArrowRight aria-hidden="true" /></a>
                <a className="home-button home-button-secondary" href="#spirituality">Explore My Spiritual Journey <FaArrowRight aria-hidden="true" /></a>
              </div>
              <div className="home-hero-identities"><span>Staff Software Engineer</span><span>Hindu Priest</span><span>Creator</span></div>
            </div>
            <div className="home-hero-portrait-wrap">
              <img className="home-hero-portrait" src={asset("main-portrait.png")} alt="Vishwaram Maharaj" width="1254" height="1254" />
            </div>
          </div>
        </section>

        <EditorialSection id="about" className="home-story">
          <SectionHeader
            eyebrow="A personal practice"
            title="Two disciplines. One purpose."
            description="I move between building dependable systems and serving the traditions that give them meaning. Both ask for attention, discipline, and care for the people on the other side."
          />
          <div className="home-story-detail">
            <span className="home-devanagari" lang="sa">धर्मेण जीवनम्</span>
            <span>A life shaped by responsibility, curiosity, and service.</span>
          </div>
        </EditorialSection>

        <EditorialSection id="technology" tone="dark" className="home-worlds">
          <SectionHeader eyebrow="Two worlds, one purpose" title="Different disciplines. The same standard." description="Technology and spirituality are different practices with the same commitment to clarity, care, and lasting work." />
          <div className="home-world-grid">
            <Link className="home-world-panel" to="/professional/projects">
              <span className="home-world-number">01 / Technology</span>
              <div><h3>Technology &amp; Innovation</h3><p>Distributed systems, financial technology, architecture, and products built to last.</p></div>
              <span className="home-panel-arrow"><FaArrowRight aria-hidden="true" /></span>
            </Link>
            <Link className="home-world-panel home-world-spirituality" to="/religious/videos">
              <span className="home-world-number">02 / Spirituality</span>
              <div><h3>Faith &amp; Spirituality</h3><p>Puja, Havan, Yajna, weddings, scripture, and service rooted in Sanatana Dharma.</p></div>
              <span className="home-panel-arrow"><FaArrowRight aria-hidden="true" /></span>
            </Link>
          </div>
        </EditorialSection>

        <EditorialSection id="experience" className="home-proof">
          <SectionHeader
            eyebrow="Professional experience"
            title="Engineering with range."
            description="A compact view of the work. Detailed responsibilities and education remain on the professional page."
            action={<ActionLink to="/professional/resume">View full experience</ActionLink>}
          />
          <div className="home-timeline">
            {experience.map(([date, company, role, summary]) => (
              <article className="home-timeline-item" key={company}>
                <div className="home-timeline-meta"><span>{date}</span><strong>{company}</strong></div>
                <div><h3>{role}</h3><p>{summary}</p></div>
              </article>
            ))}
          </div>
          <div className="home-expertise-line"><span>Java</span><span>React</span><span>PostgreSQL</span><span>Kafka</span><span>Kubernetes</span><span>Distributed systems</span></div>
        </EditorialSection>

        <EditorialSection id="spirituality" tone="dark" className="home-practice" style={{ "--section-image": `url(${asset("religious-hero.jpg")})` }}>
          <div className="home-practice-image"><img src={asset("religious-hero.jpg")} alt="A Hindu wedding ceremony beneath a decorated mandap" loading="lazy" width="1280" height="960" /></div>
          <div className="home-practice-copy">
            <SectionHeader eyebrow="Spiritual journey" title="Rooted in Tradition. Dedicated to Dharma." description="A spiritual practice expressed through sacred ceremonies, scripture, teaching, and community service." />
            <div className="home-practice-list"><div><strong>Sacred ceremonies</strong><span>Puja · Havan · Yajna · Weddings</span></div><div><strong>Scripture &amp; discourses</strong><span>Study, reflection, and sharing</span></div><div><strong>Community service</strong><span>Presence where it is needed</span></div><div><strong>Spiritual lineage</strong><span>Honoring the Guru Parampara</span></div></div>
            <ActionLink to="/religious/videos">Explore the spiritual archive</ActionLink>
          </div>
        </EditorialSection>

        <EditorialSection id="media" className="home-work">
          <SectionHeader eyebrow="Selected work &amp; media" title="Useful things, thoughtfully shared." description="A small selection of applications and public media. The complete archives stay one click away." action={<ActionLink to="/professional/projects">See all projects</ActionLink>} />
          <div className="home-project-list">
            {projects.map((project) => <a className="home-project-row" href={project.href} target="_blank" rel="noopener noreferrer" key={project.title}><img src={project.image} alt={`${project.title} app icon`} loading="lazy" width="80" height="80" /><div><span>{project.type}</span><h3>{project.title}</h3><p>{project.copy}</p></div><FaArrowRight aria-hidden="true" /></a>)}
            <Link className="home-project-row" to="/professional/apps/financial-risk"><div><span>Live application</span><h3>Financial Risk</h3><p>Explore a market-data application built into this site.</p></div><FaArrowRight aria-hidden="true" /></Link>
          </div>
          <div className="home-media-row">
            {videos.length ? videos.map((video) => <a className="home-media-item" href={video.href} target="_blank" rel="noopener noreferrer" key={video.title}><div className="home-media-thumb">{video.thumbnail ? <img src={video.thumbnail} alt="" loading="lazy" /> : null}<span><FaPlay aria-hidden="true" /></span></div><h3>{video.title}</h3></a>) : <div className="home-media-empty"><span>Media archive</span><p>The latest talks and ceremonies are available in the full public archive.</p><ActionLink to="/religious/videos">View videos</ActionLink></div>}
            <div className="home-photo-row">{photos.length ? photos.map((photo) => <Link to="/religious/photos" key={photo.id}><img src={photo.src} alt={photo.name} loading="lazy" /></Link>) : <span>Photography archive</span>}<ActionLink to="/religious/photos">View photography</ActionLink></div>
          </div>
        </EditorialSection>

        <EditorialSection id="contact" className="home-contact">
          <SectionHeader eyebrow="Contact" title="Let’s build something meaningful." description="For professional opportunities, technical conversations, ceremonies, or spiritual inquiry, you can reach me directly." />
          <div className="home-contact-grid"><a className="home-contact-primary" href="mailto:vishwaramm@gmail.com"><FaEnvelope aria-hidden="true" /><span>Professional &amp; spiritual inquiries</span><strong>vishwaramm@gmail.com</strong></a><a href="tel:9144142079"><span>Phone</span><strong>914-414-2079</strong></a><div className="home-contact-socials"><a href="https://www.linkedin.com/in/vishwaram-maharaj-98110173" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FaLinkedin /></a><a href="https://www.youtube.com/c/vishwarammaharaj" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><FaYoutube /></a><a href="https://github.com/vishwaramm" target="_blank" rel="noopener noreferrer" aria-label="GitHub"><FaGithub /></a></div></div>
        </EditorialSection>
      </main>
    </SiteLayout>
  );
}

export default HomePage;
